"use client";

import Image from 'next/image';
import Link from 'next/link';
import { BookOpen } from 'lucide-react';
import { GlowingEffect } from "@/components/ui/glowing-effect";
import { Button } from '@/components/ui/button';
import { FormationProps } from '@/types/formation';

export function FormationCardGlow({
  title,
  description,
  duration,
  category,
  imageSrc,
  slug,
  habilitation
}: FormationProps) {
  return (
    <div className="relative h-full w-full">
      <div className="relative h-full w-full rounded-[1.25rem] border border-white/15 bg-gradient-to-br from-[#0B162E] via-[#0A1224] to-[#050B18] p-2 shadow-[0_20px_80px_-50px_rgba(0,0,0,0.8)] transition-all duration-300 hover:border-white/30 md:rounded-[1.5rem] md:p-3">
        <GlowingEffect
          spread={30}
          glow={true}
          disabled={false}
          proximity={64}
          inactiveZone={0.01}
          borderWidth={2}
        />
        <div className="relative flex h-full w-full flex-col overflow-hidden rounded-xl border border-white/10 bg-white/5 p-4 shadow-sm backdrop-blur transition-all duration-300 hover:border-white/20 hover:shadow-[0_15px_60px_-45px_rgba(0,0,0,0.9)]">
          <div className="relative h-52 w-full mb-5 overflow-hidden rounded-xl border border-white/10 shadow-sm">
            <Image
              src={imageSrc || '/images/formations/default.webp'}
              alt={title}
              width={400}
              height={300}
              style={{ objectFit: "cover" }}
              className="w-full h-full bg-gray-200 transition-transform duration-700 ease-in-out hover:scale-110"
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 transition-opacity duration-300 hover:opacity-100" />
          </div>
          
          <div className="flex-1 flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <span className="inline-block rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/80">
                {category}
              </span>
              <span className="flex items-center text-xs text-white/70">
                <svg className="mr-1 h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {duration}
              </span>
            </div>
            
            <h3 className="mb-3 flex-none font-montserrat text-xl font-semibold text-white line-clamp-2">{title}</h3>

            {habilitation ? (
              <p className="mb-3 text-xs font-semibold text-accent-300">{habilitation}</p>
            ) : null}

            <p className="mb-5 flex-1 text-sm text-white/75 line-clamp-3">
              {description}
            </p>
            
            <Button asChild variant="cta" className="mt-auto gap-2 self-start text-sm">
              <Link href={`/formations/${slug}`}>
                En savoir plus
                <BookOpen className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
